import { BaseRepositoryV2 } from "@/repositories/base-repository-v2";
import type { ApiResponse } from "@/repositories/common-types";
import type {
  BillingPortalSessionResponse,
  CheckoutSessionStatusResponse,
  CouponResponse,
  CreateOneTimePaymentParams,
  CreateOneTimeCheckoutSessionResponse,
  CreateOneTimePaymentQuoteParams,
  CreateOneTimePaymentQuoteResponse,
  CreateOneTimePaymentResponse,
  CreateReportPaymentParams,
  CreateReportPaymentResponse,
  CreateSubscriptionParams,
  CreateSubscriptionResponse,
  CustomerSessionResponse,
  GetPriceResponse,
  OneTimeProductsParams,
  OneTimeProductsResponse,
  PaymentDataResponse,
  PaymentHistoryParams,
  PaymentHistoryResponse,
  PlanDto,
  StripeRepositoryMethods,
} from "@/repositories/stripe/types";

export default class StripeRepository extends BaseRepositoryV2 implements StripeRepositoryMethods {
  path: string;
  private static instance: StripeRepository;

  constructor() {
    super();
    this.path = "/payments";
  }

  public static getInstance(): StripeRepository {
    if (!StripeRepository.instance) {
      StripeRepository.instance = new StripeRepository();
    }
    return StripeRepository.instance;
  }
  public async getPaymentHistory(
    params: PaymentHistoryParams
  ): Promise<ApiResponse<PaymentHistoryResponse>> {
    const queryParams = new URLSearchParams();
    queryParams.set("userEmail", params.userEmail);
    if (params.limit !== undefined) {
      queryParams.set("limit", params.limit.toString());
    }
    if (params.startingAfter) {
      queryParams.set("startingAfter", params.startingAfter);
    }
    if (params.endingBefore) {
      queryParams.set("endingBefore", params.endingBefore);
    }
    const endpoint = `${this.path}/history/payments?${queryParams.toString()}`;
    const response = await this.get<PaymentHistoryResponse>(endpoint, {}, true);
    return this.toAxiosResponse<PaymentHistoryResponse>(response);
  }
  public async getPaymentData(userEmail: string): Promise<ApiResponse<PaymentDataResponse>> {
    const endpoint = `${this.path}/history/user-payment-data?userEmail=${userEmail}`;
    const response = await this.get<PaymentDataResponse>(endpoint, {}, true);
    return this.toAxiosResponse<PaymentDataResponse>(response);
  }
  public async getPlans(locale: string): Promise<ApiResponse<PlanDto>> {
    const queryParams = new URLSearchParams({ locale });
    const endpoint = `${this.path}/public/products?${queryParams.toString()}`;
    const response = await this.get<PlanDto>(endpoint, {}, false);
    return this.toAxiosResponse<PlanDto>(response);
  }
  public async getPrice(productId: string): Promise<ApiResponse<GetPriceResponse>> {
    const endpoint = `${this.path}/public/products/${productId}/prices`;
    const response = await this.get<GetPriceResponse>(endpoint, {}, false);
    return this.toAxiosResponse<GetPriceResponse>(response);
  }

  public async createSubscription(
    payload: CreateSubscriptionParams
  ): Promise<ApiResponse<CreateSubscriptionResponse>> {
    const endpoint = `${this.path}/subscription`;
    const response = await this.post(endpoint, payload, {}, true);
    return this.toAxiosResponse<CreateSubscriptionResponse>(response);
  }

  public async createOneTimePayment(
    payload: CreateOneTimePaymentParams
  ): Promise<ApiResponse<CreateOneTimePaymentResponse>> {
    const endpoint = `${this.path}/one-time-payment`;
    const response = await this.post(endpoint, payload, {}, true);
    return this.toAxiosResponse<CreateOneTimePaymentResponse>(response);
  }

  public async createOneTimeCheckoutSession(
    payload: CreateOneTimePaymentParams
  ): Promise<ApiResponse<CreateOneTimeCheckoutSessionResponse>> {
    const endpoint = `${this.path}/one-time-payment/checkout-session`;
    const response = await this.post(endpoint, payload, {}, true);
    return this.toAxiosResponse<CreateOneTimeCheckoutSessionResponse>(response);
  }

  public async getOneTimeCheckoutSession(
    sessionId: string
  ): Promise<ApiResponse<CheckoutSessionStatusResponse>> {
    const endpoint = `${this.path}/one-time-payment/checkout-session/${sessionId}`;
    const response = await this.get<CheckoutSessionStatusResponse>(endpoint, {}, true);
    return this.toAxiosResponse<CheckoutSessionStatusResponse>(response);
  }

  public async createOneTimePaymentQuote(
    payload: CreateOneTimePaymentQuoteParams
  ): Promise<ApiResponse<CreateOneTimePaymentQuoteResponse>> {
    const endpoint = `${this.path}/one-time-payment/quote`;
    const response = await this.post(endpoint, payload, {}, true);
    return this.toAxiosResponse<CreateOneTimePaymentQuoteResponse>(response);
  }

  public async createCustomerSession(
    customerEmail: string
  ): Promise<ApiResponse<CustomerSessionResponse>> {
    const endpoint = `${this.path}/customer-session`;
    const response = await this.post(endpoint, { customerEmail }, {}, true);
    return this.toAxiosResponse<CustomerSessionResponse>(response);
  }

  public async createBillingPortalSession(
    customerEmail: string
  ): Promise<ApiResponse<BillingPortalSessionResponse>> {
    const endpoint = `${this.path}/billing-portal-session`;
    const response = await this.post(endpoint, { customerEmail }, {}, true);
    return this.toAxiosResponse<BillingPortalSessionResponse>(response);
  }

  public async getCoupon(couponId: string): Promise<ApiResponse<CouponResponse>> {
    const endpoint = `${this.path}/coupons/${couponId}`;
    const response = await this.get<CouponResponse>(endpoint, {}, true);
    return this.toAxiosResponse<CouponResponse>(response);
  }

  public async getOneTimeProducts(
    params?: OneTimeProductsParams
  ): Promise<ApiResponse<OneTimeProductsResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) {
      queryParams.set("limit", params.limit.toString());
    }
    if (params?.active !== undefined) {
      queryParams.set("active", params.active.toString());
    }
    const queryString = queryParams.toString();
    const endpoint = `${this.path}/public/products/one-time${queryString ? `?${queryString}` : ""}`;
    const response = await this.get<OneTimeProductsResponse>(endpoint, {}, false);
    return this.toAxiosResponse<OneTimeProductsResponse>(response);
  }

  public async createReportPayment(
    payload: CreateReportPaymentParams
  ): Promise<ApiResponse<CreateReportPaymentResponse>> {
    const endpoint = `${this.path}/report-payment`;
    const response = await this.post(endpoint, payload, {}, true);
    return this.toAxiosResponse<CreateReportPaymentResponse>(response);
  }

  public async abandonOneTimePayment(paymentIntentId: string, reason?: string): Promise<void> {
    await this.post(`${this.path}/one-time-payment/abandon`, { paymentIntentId, reason }, {}, true);
  }
}
